import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'react-bootstrap';
import { SIZE } from 'baseui/button';
import store from 'store';
import { paths } from 'frameworks_drivers/routes';
import { TrackerUtil } from 'tracking/TrackerUtil';
import TitleWithPrevPageLink from 'presentation/components/TitleWithPrevPageLink';
import { WizardSteps, WizardStepsContainer } from 'presentation/components/WizardSteps';
import option1Icon from 'presentation/assets/icons/png/3x/icn-ja@3x.png';
import option2Icon from 'presentation/assets/icons/png/3x/icn-nein@3x.png';
import { RadioOptionKeys, translationKeyForRadioOption } from 'tools/RadioOptionMapper';
import { setCentralHotWaterDelivery as setCentralHotWaterDeliveryInStore } from 'frameworks_drivers/redux/reducers/onboardingInformation/onboardingInformationSlice';
import PageDescription from 'presentation/components/PageDescription';
import IconRadioGroup from 'presentation/components/IconRadioGroup';
import { Button } from '@techem/ui-components';
import { useNavigate } from 'react-router-dom';
import { DISABLE_FLOW_DEEP_LINKING_PROTECTION } from 'config';
import useHotKey from 'tools/hooks/useHotKey';

const HotWaterDeliveryPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const prevPath = paths.CentralHeating;

  useEffect(() => {
    if (
      !DISABLE_FLOW_DEEP_LINKING_PROTECTION &&
      // Assumption: previous page has to be filled with user input before the user is allowed to visit this page
      store.getState().onboardingInfo.centralHeatingAvailable === ''
    ) {
      navigate(prevPath, { replace: true });
    }
  }, [prevPath, navigate]);

  const [centralHotWaterDelivery, setCentralHotWaterDelivery] = useState<string>(
    store.getState().onboardingInfo.centralHotWaterDelivery
  );

  const radioData = [
    {
      icon: option1Icon,
      key: RadioOptionKeys.HotWaterDeliveryCentral.Yes,
      trackingId: 'radio-button_yes'
    },
    {
      icon: option2Icon,
      key: RadioOptionKeys.HotWaterDeliveryCentral.No,
      trackingId: 'radio-button_no'
    }
  ].map((opt) => ({
    icon: opt.icon,
    label: t(translationKeyForRadioOption(opt.key)),
    optionKey: opt.key,
    trackingId: opt.trackingId
  }));

  useHotKey((event) => {
    if (event.key === '1') setCentralHotWaterDelivery(radioData[0].optionKey);
    if (event.key === '2') setCentralHotWaterDelivery(radioData[1].optionKey);
  });

  const handleSubmit = (e: React.FormEvent) => {
    store.dispatch(setCentralHotWaterDeliveryInStore(centralHotWaterDelivery));

    TrackerUtil.trackNextBtnClick();
    navigate(paths.HeatingType);

    e.preventDefault();
  };

  const canBeSubmitted = () => {
    return centralHotWaterDelivery !== '';
  };

  const handlePrevBtnClicked = () => {
    TrackerUtil.trackPrevBtnClick();
    navigate(prevPath);
  };

  return (
    <Container className="pb-4 mb-5">
      <WizardStepsContainer>
        <WizardSteps currentStep={4} totalSteps={6} />
      </WizardStepsContainer>
      <TitleWithPrevPageLink
        onPrevClick={handlePrevBtnClicked}
        title={t('pageHotWaterDeliveryTitle')}
      />
      <PageDescription indented>{t('pageHotWaterDeliveryDescription')}</PageDescription>

      <form onSubmit={handleSubmit}>
        <Row className="justify-content-center mt-4">
          <Col lg={6} md={8} className="text-center">
            <fieldset>
              <legend id="hotwater-label" className="visually-hidden">
                {t('pageHotWaterDeliveryTitle')}
              </legend>
              <IconRadioGroup
                radioData={radioData}
                handleChange={(newVal: string) => setCentralHotWaterDelivery(newVal)}
                currentSelection={() => centralHotWaterDelivery}
                aria-labelledby="hotwater-label"
              />
            </fieldset>
          </Col>
        </Row>

        <Row className="justify-content-center py-5">
          <Col className="text-center col-auto">
            <Button
              disabled={!canBeSubmitted()}
              size={SIZE.large}
              type="submit"
              additionalTestId="next"
            >
              {t('nextBtnText')}
            </Button>
          </Col>
        </Row>
      </form>
    </Container>
  );
};

export default HotWaterDeliveryPage;
